import {Box, Card, Container, Divider, Flex, Grid, Group, Menu, rem, Text, Title, UnstyledButton} from "@mantine/core";
import {useCallback, useMemo} from "react";
import {
  getUseQueryResultData,
  selectServiceEventsAndTransform,
  ServiceEventBadgeSummary,
  useGetServiceEventBadgeSummaryQuery,
} from "@ots-frontend/ots-data";
import {IconChevronDown} from "@tabler/icons-react";
import {useTranslation} from "react-i18next";
import {skipToken} from "@reduxjs/toolkit/query";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {ResizeableContent} from "../../components/ResizableContent/ResizableContent";
import {PAGINATION_PAGE_SIZES} from "../../components/data-table/DataTable/usePagination";
import {useNavigate} from "react-router-dom";
import {RouteBreadcrumbs} from "../../components/breadcrumb/RouteBreadcrumbs/RouteBreadcrumbs";
import {HasAccess} from "../../components/auth/HasAccess/HasAccess";
import {createSelector} from "@reduxjs/toolkit";
import {Insight} from "../../components/insights/Insight/Insight";
import {ServiceEventDataTable} from "../../components/data-table/ServiceEventDataTable/ServiceEventDataTable";

export type OpsDashboardPageProps = {}

export const OpsDashboardPage = (props: OpsDashboardPageProps) => {
  const {regionId} = useApp();
  const {t} = useTranslation(["common"]);

  const navigate = useNavigate()

  const selectBadgeSummary = useMemo(() => {
    return createSelector(
        [getUseQueryResultData<ServiceEventBadgeSummary>],
        (data) => {
          return {
            ...data ?? {},
            recentEvents: selectServiceEventsAndTransform(data?.recentEvents),
          }
        }
    )
  }, []);

  const {badge, isFetchingBadge, refetch: refetchBadge} = useGetServiceEventBadgeSummaryQuery(regionId ?? skipToken, {
    selectFromResult: (result) => ({
      ...result,
      badge: selectBadgeSummary(result),
      isFetchingBadge: result.isFetching,
    })
  });

  const onRefreshClick = useCallback(() => {
    if (refetchBadge) {
      refetchBadge()
    }
  }, [refetchBadge])

  const onSearchClick = useCallback(() => {
    navigate("/service-events")
  }, [navigate])

  return (
      <ThemedPage>
        {(classes) => (
            <ResizeableContent>
              {(size) => (
                  <Container h={size.height} w={size.width} className={classes.container} fluid={true} p="md">
                    <Flex direction="column" h="100%" m="sm" gap="sm">
                      <RouteBreadcrumbs/>
                      <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
                        <Group position="apart">
                          <Title order={4}>
                            {t("common.page.ops.dashboard.title", "Ops Dashboard")}
                          </Title>
                          <Menu
                              width={260}
                              position="bottom-end"
                              transitionProps={{transition: "pop-top-right"}}
                              withinPortal
                          >
                            <Menu.Target>
                              <UnstyledButton>
                                <Group spacing={7}>
                                  <Text weight={500} size="sm" sx={{lineHeight: 1}} mr={3}>
                                    {t('common.action.plural', "Actions")}
                                  </Text>
                                  <IconChevronDown size={rem(12)} stroke={1.5}/>
                                </Group>
                              </UnstyledButton>
                            </Menu.Target>
                            <Menu.Dropdown>
                              <Menu.Item onClick={onRefreshClick}>
                                {t("common.action.refresh", "Refresh", {ns: "common"})}
                              </Menu.Item>
                              <Menu.Item onClick={onSearchClick}>
                                {t("common.action.search", "Search", {ns: "common"}) + " " + t("common.entity.serviceevent.plural.", "Service Events", {ns: "common"})}
                              </Menu.Item>
                              <HasAccess permissions={["serviceevent_create"]}>
                                <>
                                  <Menu.Divider/>
                                  <Menu.Item onClick={() => navigate("/service-events/form")}>
                                    {t("common.action.new", "New", {ns: "common"}) + " " + t("common.entity.serviceevent", "Service Event", {ns: "common"})}
                                  </Menu.Item>
                                </>
                              </HasAccess>
                            </Menu.Dropdown>
                          </Menu>
                        </Group>
                      </Card>

                      <Box sx={{flex: 1, overflow: 'auto'}}>
                        <Grid>

                          <Grid.Col span={3}>
                            <Insight
                                loading={isFetchingBadge}
                                title={t<string>("common.page.ops.dashboard.badge.serviceevent.ongoing", "Ongoing Service Events")}
                                icon={"chartHistogram"}
                                value={badge.ongoingEvents}/>
                          </Grid.Col>


                          <Grid.Col span={3}>
                            <Insight
                                loading={isFetchingBadge}
                                title={t<string>("common.page.ops.dashboard.badge.serviceevent.total", "Total Service Events")}
                                icon={"chartHistogram"}
                                value={badge.totalEvents}/>
                          </Grid.Col>


                          <Grid.Col span={3}>
                            <Insight
                                loading={isFetchingBadge}
                                title={t<string>("common.page.ops.dashboard.badge.serviceevent.completed", "Completed Service Events")}
                                icon={"chartHistogram"}
                                value={badge.completedEvents}/>
                          </Grid.Col>

                          <Grid.Col span={12}>
                            <Card shadow="sm" padding="lg" radius="md" withBorder>

                              <Group position="apart" pb="sm">
                                <Title order={4}>
                                  {t("common.page.ops.dashboard.section.recent", "Recent Service Events")}
                                </Title>
                              </Group>

                              <Divider/>

                              <ServiceEventDataTable
                                  withBorder
                                  records={badge.recentEvents}
                                  recordsPerPageOptions={PAGINATION_PAGE_SIZES}
                                  fetching={isFetchingBadge}/>

                            </Card>
                          </Grid.Col>
                        </Grid>
                      </Box>
                    </Flex>
                  </Container>
              )}
            </ResizeableContent>
        )}
      </ThemedPage>
  );
};
